import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'

type UnknownRecord = Record<string, unknown>

const unwrapData = <T>(payload: unknown): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return ((payload as UnknownRecord).data ?? payload) as T
  }
  return payload as T
}

export type MetaConnection = {
  id?: string | number
  service_instance?: string | number
  service_instance_identifier?: string | null
  meta_business_id?: string | null
  whatsapp_business_account_id?: string | null
  phone_number_id?: string | null
  catalog_id?: string | null
  status?: string | null
  default_order_type_key?: string | null
  auto_place_orders?: boolean | null
  selected_assets?: Record<string, unknown> | null
  has_access_token?: boolean
  last_error?: string | null
}

export type MetaAssetSelectionPayload = {
  meta_business_id?: string
  whatsapp_business_account_id?: string
  phone_number_id?: string
  catalog_id?: string
  default_order_type_key?: string
  auto_place_orders?: boolean
  selected_assets?: Record<string, unknown>
}

export type MetaOauthStartResponse = {
  oauth_url: string
  state: string
  redirect_uri: string
}

export type MetaCatalogPreview = {
  count?: number
  items?: Array<Record<string, unknown>>
  errors?: Array<Record<string, unknown>>
}

export type MetaCatalogCreatePayload = {
  name: string
  vertical?: string
  auto_select?: boolean
}

export type MetaSyncRun = {
  id: string | number
  status?: string | null
  total_count?: number
  success_count?: number
  failed_count?: number
  skipped_count?: number
  created_on?: string | null
  started_at?: string | null
  finished_at?: string | null
  last_error?: string | null
}

export type MetaCatalogMapping = {
  id: string | number
  service_instance?: string | number
  product?: string | number | null
  product_variant?: string | number | null
  product_name?: string | null
  product_variant_label?: string | null
  retailer_id?: string | null
  meta_catalog_id?: string | null
  meta_item_id?: string | null
  sync_status?: string | null
  last_error?: string | null
  last_payload?: Record<string, unknown> | null
  last_synced_at?: string | null
  updated_on?: string | null
}

export type MetaWebhookEvent = {
  id: string | number
  event_id?: string | null
  raw_payload?: Record<string, unknown> | null
  status?: string | null
  message_id?: string | null
  phone_number_id?: string | null
  received_at?: string | null
  processed_at?: string | null
  linked_order_id?: string | number | null
  error?: string | null
}

export type MetaOrderTypeOption = {
  id?: string | number
  key: string
  label: string
  active?: boolean
}

const serviceInstanceParams = (serviceInstanceId: string | number) => ({
  service_instance_id: serviceInstanceId,
})

const listPayload = <T>(payload: unknown): T[] => {
  const data = unwrapData<unknown>(payload)
  if (Array.isArray(data)) {
    return data as T[]
  }
  if (data && typeof data === 'object') {
    const record = data as UnknownRecord
    if (Array.isArray(record.results)) {
      return record.results as T[]
    }
    if (record.data && typeof record.data === 'object' && Array.isArray((record.data as UnknownRecord).results)) {
      return (record.data as UnknownRecord).results as T[]
    }
  }
  return []
}

export const metaApi = {
  getConnection: async (serviceInstanceId: string | number): Promise<MetaConnection | null> => {
    try {
      const response = await api.get(API_PATHS.meta.connectionGet, {
        params: serviceInstanceParams(serviceInstanceId),
      })
      return unwrapData<MetaConnection>(response.data)
    } catch {
      return null
    }
  },

  startOAuth: async (serviceInstanceId: string | number, redirectUri: string): Promise<MetaOauthStartResponse> => {
    const response = await api.get(API_PATHS.meta.oauthStart, {
      params: {
        ...serviceInstanceParams(serviceInstanceId),
        redirect_uri: redirectUri,
      },
    })
    return unwrapData<MetaOauthStartResponse>(response.data)
  },

  completeOAuth: async (params: {
    code: string
    state: string
    redirectUri: string
  }): Promise<{ connection?: MetaConnection }> => {
    const response = await api.get(API_PATHS.meta.oauthCallback, {
      params: {
        code: params.code,
        state: params.state,
        redirect_uri: params.redirectUri,
      },
    })
    return unwrapData<{ connection?: MetaConnection }>(response.data)
  },

  selectAssets: async (
    serviceInstanceId: string | number,
    payload: MetaAssetSelectionPayload,
  ): Promise<MetaConnection> => {
    const response = await api.post(
      API_PATHS.meta.connectionSelectAssets,
      {
        ...payload,
        service_instance_id: serviceInstanceId,
      },
    )
    return unwrapData<MetaConnection>(response.data)
  },

  disconnect: async (serviceInstanceId: string | number): Promise<MetaConnection> => {
    const response = await api.post(API_PATHS.meta.connectionDisconnect, {
      service_instance_id: serviceInstanceId,
    })
    return unwrapData<MetaConnection>(response.data)
  },

  previewCatalog: async (serviceInstanceId: string | number): Promise<MetaCatalogPreview> => {
    const response = await api.post(API_PATHS.meta.catalogPreview, {
      service_instance_id: serviceInstanceId,
    })
    return unwrapData<MetaCatalogPreview>(response.data)
  },

  createCatalog: async (
    serviceInstanceId: string | number,
    payload: MetaCatalogCreatePayload,
  ): Promise<{ connection?: MetaConnection; catalog?: Record<string, unknown> }> => {
    const response = await api.post(API_PATHS.meta.catalogCreate, {
      ...payload,
      service_instance_id: serviceInstanceId,
    })
    return unwrapData<{ connection?: MetaConnection; catalog?: Record<string, unknown> }>(response.data)
  },

  syncCatalog: async (serviceInstanceId: string | number): Promise<MetaSyncRun> => {
    const response = await api.post(API_PATHS.meta.catalogSync, {
      service_instance_id: serviceInstanceId,
    })
    return unwrapData<MetaSyncRun>(response.data)
  },

  syncCatalogItem: async (
    serviceInstanceId: string | number,
    productVariantId: string | number,
  ): Promise<{ mapping?: MetaCatalogMapping; meta_response?: unknown }> => {
    const response = await api.post(API_PATHS.meta.catalogItemSync, {
      ...serviceInstanceParams(serviceInstanceId),
      product_variant_id: productVariantId,
    })
    return unwrapData<{ mapping?: MetaCatalogMapping; meta_response?: unknown }>(response.data)
  },

  removeCatalogItem: async (
    serviceInstanceId: string | number,
    productVariantId: string | number,
  ): Promise<{ mapping?: MetaCatalogMapping | null; meta_response?: unknown }> => {
    const response = await api.post(API_PATHS.meta.catalogItemRemove, {
      ...serviceInstanceParams(serviceInstanceId),
      product_variant_id: productVariantId,
    })
    return unwrapData<{ mapping?: MetaCatalogMapping | null; meta_response?: unknown }>(response.data)
  },

  listSyncRuns: async (serviceInstanceId: string | number): Promise<MetaSyncRun[]> => {
    const response = await api.get(API_PATHS.meta.syncRunsList, {
      params: serviceInstanceParams(serviceInstanceId),
    })
    return unwrapData<MetaSyncRun[]>(response.data) ?? []
  },

  listCatalogItems: async (serviceInstanceId: string | number): Promise<MetaCatalogMapping[]> => {
    const response = await api.get(API_PATHS.meta.catalogItemsList, {
      params: serviceInstanceParams(serviceInstanceId),
    })
    return unwrapData<MetaCatalogMapping[]>(response.data) ?? []
  },

  listWebhookEvents: async (serviceInstanceId: string | number): Promise<MetaWebhookEvent[]> => {
    const response = await api.get(API_PATHS.meta.webhookEventsList, {
      params: serviceInstanceParams(serviceInstanceId),
    })
    return unwrapData<MetaWebhookEvent[]>(response.data) ?? []
  },

  sendWhatsAppText: async (
    serviceInstanceId: string | number,
    payload: { to: string; body: string },
  ): Promise<unknown> => {
    const response = await api.post(API_PATHS.meta.messageSend, {
      ...payload,
      service_instance_id: serviceInstanceId,
    })
    return unwrapData<unknown>(response.data)
  },

  listOrderTypes: async (serviceInstanceId: string | number): Promise<MetaOrderTypeOption[]> => {
    const response = await api.get(API_PATHS.store.orderTypes.list, {
      params: {
        ...serviceInstanceParams(serviceInstanceId),
        simple: true,
        active: true,
      },
    })
    return listPayload<Record<string, unknown>>(response.data)
      .map((row) => {
        const key = String(row.key ?? '').trim()
        return {
          id: row.id as string | number | undefined,
          key,
          label: String(row.label ?? key).trim() || key,
          active: row.active as boolean | undefined,
        }
      })
      .filter((row) => row.key)
  },
}
